import { evolve, omit, piped } from 'remeda';

import {
    type Article,
    type DetailedRevision,
    type DetailedUnityRevision,
    isArticle,
    type NewArticle,
    type Revision,
    type UnityArticle,
} from '@@api/services/content/schemas/article';
import deserializeState from '@@editor/serialization/deserializeState';
import serializeState from '@@editor/serialization/serializeState';
import unwrapSerializedState from '@@editor/serialization/UnitySerializer/unwrapSerializedState';
import wrapSerializedState from '@@editor/serialization/UnitySerializer/wrapSerializedState';
import { logDuplicateEvent, removeDuplicates } from '@@editor/utils/removeDuplicates';

import {
    getVariantsFieldsFromContent,
    removeUnwantedElements,
    transformToArticleVariants,
} from './utils/deserialization';
import { transformToArticleContent } from './utils/serialization';

const deserializeContentState = (state) => deserializeState(state);
const serializeContentState = (state) => serializeState(state);

const transformVariants = piped(getVariantsFieldsFromContent, transformToArticleVariants);

const contentTransformations = piped(
    unwrapSerializedState,
    removeUnwantedElements,
    wrapSerializedState,
    deserializeContentState,
);

export const deserializeArticle = (entity: UnityArticle): Article =>
    // If the article is a Unity article, we need to get the variants out of the content
    entity.contentOwner === 'UNITY'
        ? evolve(
              { ...entity, variants: transformVariants(entity) },
              {
                  content: contentTransformations,
              },
          )
        : // Else we just transform the content
          evolve(
              // Even though `variants` is not used in non-Unity articles, we add it for type simplicity
              { ...entity, variants: [] },
              {
                  content: deserializeContentState,
              },
          );

export const serializeArticle = (entity: Article | NewArticle): UnityArticle | NewArticle => {
    // When a article is cloned, we will have no data in the body except metadata. So only convert
    // the content if it already exists on the entity. We need create a futureproof solution here...
    if (isArticle(entity)) {
        const newContent = removeDuplicates(transformToArticleContent(entity));

        logDuplicateEvent(newContent.isModified, {
            values: newContent.originalValue,
            articleId: entity.id,
        });

        return evolve(omit(entity, ['variants']), {
            content: () => serializeContentState(newContent.filteredValue),
        });
    }

    return entity;
};

export const deserializeRevision = (entity: Revision): Revision => entity;

export const deserializeDetailedRevision = (entity: DetailedUnityRevision): DetailedRevision => {
    const revisionData = {
        ...entity.revisionData,
        variants: transformVariants(entity.revisionData),
    };

    const deserializedRevisionData = evolve(revisionData, {
        content: contentTransformations,
    });

    return {
        ...entity,
        revisionData: deserializedRevisionData,
    };
};
